import React, { Dispatch, SetStateAction, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { TagsInput } from "react-tag-input-component";

import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { expressions } from '../../../../libraries/regular.expressions.library';
import { legendInvalid, legendValid, legendValidInvalidRestart } from '../../../../utils/legend.data.util';
import { evaluateLegendRequiredIcon } from '../../../../scripts/validate.legend.script';
import { modalShow } from '../../../../utils/modal.util';
import { Brand } from '../../../../models/brands.inventory.model';

import AppLegend from '../../../../components/element/Legend';

export interface AppInventoryFormGeneralProps {
  inventory: string,
  action: string,
  display: string,
  other: boolean,
  name: {value: string, valid: boolean},
  brand?: {value: string, valid: boolean},
  tags: {value: string[], valid: boolean},
  brandsForCompany?: Brand[],
  setName: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setBrand?: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setTags: Dispatch<SetStateAction<{value: string[], valid: boolean}>>
};

const AppInventoryFormGeneral: React.FunctionComponent<AppInventoryFormGeneralProps> = ({inventory, action, display, other, name, brand, tags, brandsForCompany, setName, setBrand, setTags}) => {
  const {lang} = StateLanguage()
  const [brandSearch, setBrandSearch] = useState('')

  const handleChangeName = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setName({...name, value: event.target.value})
  }

  const handleSelectBrand = (item: Brand) => {
    if (setBrand) {
      setBrand({value: item.id, valid: true})
    }
  }

  const handleClearBrand = () => {
    if (setBrand) {
      setBrand({value: '', valid: true})
      setBrandSearch('')
    }
  }

  const validateName = () => {
    evaluateLegendRequiredIcon(expressions.name, name, setName, 'input-name', 'legend-name-valid', 'legend-name-required')
    evaluateLegendRequiredIcon(expressions.name, name, setName, 'input-name-other', 'legend-name-other-valid', 'legend-name-other-required')
  }

  const handleChangeTags = (items: string[]) => {
    setTags({...tags, value: items})
  }

  const validateTagKeyUp = (event: React.KeyboardEvent <HTMLFormElement | HTMLInputElement>) => {
    if (expressions && expressions.tag.test(event.currentTarget.value)) {
      if (!tags.value.includes(event.currentTarget.value)) {
        setTags({...tags, valid: true})
        legendValid('legend-tags-valid')
        legendValid('legend-tags-exists')
        legendValid('legend-tags-other-valid')
        legendValid('legend-tags-other-exists')
      } else {
        setTags({...tags, valid: false})
        legendInvalid('legend-tags-exists')
        legendInvalid('legend-tags-other-exists')
      }
    } else {
      setTags({...tags, valid: false})
      legendInvalid('legend-tags-valid')
      legendInvalid('legend-tags-other-valid')
    }
  }

  const validateTagBlur = (event: React.FocusEvent <HTMLFormElement | HTMLInputElement>) => {
    /* if (expressions && event.currentTarget.value.length > 0 && expressions.tag.test(event.currentTarget.value) && !tags.value.includes(event.currentTarget.value)) {
      setTags({...tags, value: [...tags.value, event.currentTarget.value], valid: true})
      legendValid('legend-tags-required')
      legendValid('legend-tags-other-required')
    } else if (tags.value.length === 0) {
      setTags({...tags, valid: false})
      legendInvalid('legend-tags-required')
      legendInvalid('legend-tags-other-required')
    } */

    if (tags.value.includes(event.currentTarget.value)) {
      setTags({...tags, valid: true})
      legendValidInvalidRestart('legend-tags-exists')
      legendValidInvalidRestart('legend-tags-other-exists')
    }

    event.currentTarget.value = ''
    legendValidInvalidRestart('legend-tags-valid')
    legendValidInvalidRestart('legend-tags-other-valid')
  }

  const validateTagAdd = (item: string, items: string[]) => {
    if (expressions && items) {
      if (expressions.tag.test(item)) {
        setTags({...tags, valid: true})
        legendValid('legend-tags-valid')
        //legendValid('legend-tags-required')
        legendValid('legend-tags-other-valid')
        //legendValid('legend-tags-other-required')
        return true;
      } else {
        setTags({...tags, valid: false})
        legendInvalid('legend-tags-valid')
        legendInvalid('legend-tags-other-valid')
        return false;
      }
    } else {
      setTags({...tags, valid: false})
      legendValidInvalidRestart('legend-tags-valid')
      //legendValidInvalidRestart('legend-tags-required')
      legendValidInvalidRestart('legend-tags-other-valid')
      //legendValidInvalidRestart('legend-tags-other-required')
      return false;
    }
  }

  const validateTagExisting = (item: string) => {
    if (item.length > 0) {
      setTags({...tags, valid: false})
      legendInvalid('legend-tags-exists')
      legendInvalid('legend-tags-other-exists')
    } else {
      setTags({...tags, valid: true})
      legendValid('legend-tags-exists')
      legendValid('legend-tags-other-exists')
    }
  }

  const validateTagRemoved = (item: string) => {
    /* if (item.length > 0 && tags.value.length === 1) {
      setTags({...tags, valid: false})
      legendInvalid('legend-tags-required')
      legendInvalid('legend-tags-other-required')
    } else {
      setTags({...tags, valid: true})
      legendValid('legend-tags-required')
      legendValid('legend-tags-other-required')
    } */

    setTags({...tags, valid: true})
  }

  return (
    <div className={`card card-flush ${display}`}>
      <div className="card-header">
        <div className="card-title">
          <h3>{lang.labels.general}</h3>
        </div>
      </div>
      <div className="card-body pt-0">
        <div className="mb-3">
          <label className="form-label required">{lang.labels[`${inventory}Name` as keyof typeof lang.labels]}</label>
          <input id={other ? "input-name-other" : "input-name"} className="form-control" type="text" autoComplete="off" name="name" value={name.value} onChange={handleChangeName} onKeyUp={validateName} onBlur={validateName} />
          <div className={`form-text text-justify ${action === "update" && "d-none"}`}>{lang.labels[`${inventory}NameRequiredAndRecommendedUnique` as keyof typeof lang.labels]}</div>
          <AppLegend attribute={{check: name.valid, name: other ? "name-other" : "name", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
        </div>
        { brand && setBrand && brandsForCompany &&
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="form-label mb-0">{lang.labels.brand}</label>
              <button className="btn btn-sm btn-light-primary p-1 px-2" type="button" onClick={() => modalShow('modal-brand')}>
                <span className="fs-9">+ {lang.labels.addBrand}</span>
              </button>
            </div>
            <Dropdown>
              <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                <span className="selection">
                  <span className="select2-selection select2-selection--single form-select">
                    <span className="select2-selection__rendered" role="textbox">
                      {brand.value ? (brandsForCompany.find(b => b.id === brand.value)?.name ?? lang.labels.selectOption) : lang.labels.selectOption}
                    </span>
                  </span>
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                <span className="select2-dropdown select2-dropdown--below">
                  <span className="select2-search select2-search--dropdown">
                    <input className="select2-search__field form-control" type="text" autoComplete="off" placeholder={lang.labels.searchBrand} value={brandSearch} onChange={(e) => setBrandSearch(e.target.value)} />
                  </span>
                  <span className="select2-results">
                    <ul className="select2-results__options" role="listbox">
                      { brand.value &&
                        <li className="select2-results__option select2-results__option--selectable">
                          <Dropdown.Item bsPrefix="select2-results__option__text text-muted" onClick={handleClearBrand}>{lang.labels.clear}</Dropdown.Item>
                        </li>
                      }
                      { brandsForCompany.filter(b => b.name.toLowerCase().includes(brandSearch.toLowerCase())).length === 0 &&
                        <li className="select2-results__option select2-results__option--selectable">
                          <Dropdown.Item bsPrefix="select2-results__option__text">{lang.labels.selectOption}</Dropdown.Item>
                        </li>
                      }
                      { brandsForCompany.filter(b => b.name.toLowerCase().includes(brandSearch.toLowerCase())).map((item, index) => { return (
                        <li key={index} className={`select2-results__option select2-results__option--selectable ${item.id === brand.value && "select2-results__option--selected"}`} role="option" aria-selected={item.id === brand.value}>
                          <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleSelectBrand(item)}>{item.name}</Dropdown.Item>
                        </li>
                      )})}
                    </ul>
                  </span>
                </span>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        }
        <div className="mb-0">
          <label className="form-label">{lang.labels.tags}</label>
          <TagsInput classNames={{tag: "tag-cls bg-dark bg-opacity-10 fs-7", input: "input-cls form-control w-100"}} name="tags" value={tags.value} onChange={handleChangeTags} onKeyUp={validateTagKeyUp} onBlur={validateTagBlur} beforeAddValidate={validateTagAdd} onExisting={validateTagExisting} onRemoved={validateTagRemoved} />
          <div className={`form-text text-justify ${action === "update" && "d-none"}`}>{lang.labels[`addTagsTo${inventory.charAt(0).toUpperCase()}${inventory.slice(1)}` as keyof typeof lang.labels]}</div>
          <AppLegend attribute={{check: tags.valid, name: other ? "tags-other" : "tags", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: true}}></AppLegend>
        </div>
      </div>
    </div>
  )
};

export default AppInventoryFormGeneral;
